import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { TutorTask } from './tutor-tasks/interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { TutorTaskModal } from './tutor-tasks/enums'
import { useDisciplineTasksStore } from './tasks/store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { isTaskOpen } from './tutor-tasks/tutor-tasks'

interface DisciplineTaskActionsOptions {
  task: ComputedRef<TutorTask>
}

interface DisciplineTaskActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  canManageTutorTasks: ComputedRef<boolean>
  refIsTaskOpen: ComputedRef<boolean>
  toggle: () => void
  openEdit: () => void
  openPostpone: () => void
  openCancel: () => void
  openDelete: () => void
}

/** Row menu of a task (and the shortcut menu of the edit modal): every action replaces the open modal. */
export const useDisciplineTaskActions = ({ task }: DisciplineTaskActionsOptions): DisciplineTaskActionsReturn => {
  const store = useDisciplineTasksStore()
  const { canManageTutorTasks } = useDisciplinePrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const refIsTaskOpen = computed<boolean>(() => isTaskOpen(task.value.status))

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const open = (modal: TutorTaskModal): void => {
    isOpen.value = false
    store.openModal(modal, task.value)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    canManageTutorTasks,
    refIsTaskOpen,
    toggle,
    openEdit: () => open(TutorTaskModal.Edit),
    openPostpone: () => open(TutorTaskModal.Postpone),
    openCancel: () => open(TutorTaskModal.Cancel),
    openDelete: () => open(TutorTaskModal.Delete),
  }
}
